import axios from 'axios';
import cfac22 from '../../cfac22';
import {
  isAccessTokenDueForRefresh,
  refreshAccessToken,
} from './authSessionManager';
import { validateSessions } from './validateSessions';
import {
  clearAuthFlowLocalContext,
  getStoredAuthData,
  hasCompleteAuthSession,
} from 'js/library/services/authSession';

let requestInterceptorId = null;
let responseInterceptorId = null;
let authRecoveryInFlightPromise = null;

const getAuthenticatedHosts = () =>
  [cfac22('API_HOST_V1'), cfac22('API_HOST_V2'), cfac22('MICROSERVICES')].filter(Boolean);

const isAuthenticatedHost = (url) => {
  if (typeof url !== 'string') return false;

  return getAuthenticatedHosts().some((host) => url.startsWith(host));
};

const isAuthRecoveryControlEndpoint = (url) => {
  if (typeof url !== 'string') return false;

  // Login, guest, logout, validate e refresh não iniciam outro ciclo de recuperação.
  return /\/auth(?:\/|$)/.test(url);
};

const getPersistedAccessToken = () => getStoredAuthData()?.accessToken || null;

const clearInvalidLocalSession = () => {
  clearAuthFlowLocalContext();

  if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
    window.location.replace('/login');
  }
};

const canRefreshRequest = (config) =>
  Boolean(
    config &&
      !config._authRefreshRetried &&
      !config._authPreflightCompleted &&
      !config._skipAuthRefresh &&
      isAuthenticatedHost(config.url) &&
      !isAuthRecoveryControlEndpoint(config.url) &&
      hasCompleteAuthSession()
  );

const isProtectedRequest = (config) =>
  Boolean(
    config &&
      !config._skipAuthValidation &&
      isAuthenticatedHost(config.url) &&
      !isAuthRecoveryControlEndpoint(config.url) &&
      hasCompleteAuthSession()
  );

const validateAndRefreshIfRequired = ({ force = false } = {}) => {
  if (authRecoveryInFlightPromise) return authRecoveryInFlightPromise;

  authRecoveryInFlightPromise = (async () => {
    const validation = await validateSessions({ force, refreshOnRequired: false });

    if (validation?.success !== true) {
      throw new Error(validation?.error || 'Sessão inválida');
    }

    const accessToken = getPersistedAccessToken();
    const shouldRefresh =
      validation.requiresRefresh === true || isAccessTokenDueForRefresh(accessToken);

    if (shouldRefresh) {
      await refreshAccessToken();
    }

    return validation;
  })().finally(() => {
    authRecoveryInFlightPromise = null;
  });

  return authRecoveryInFlightPromise;
};

const prepareProtectedRequest = async (config) => {
  if (!isProtectedRequest(config)) return config;

  await validateAndRefreshIfRequired();

  if (!hasCompleteAuthSession()) {
    clearInvalidLocalSession();
    const error = new Error('Sessão inválida ou expirada');
    error.code = 'AUTH_SESSION_REQUIRED';
    throw error;
  }

  return {
    ...config,
    withCredentials: true,
    _authPreflightCompleted: true,
  };
};

const retryAfterValidationAndRefresh = async (error) => {
  const originalRequest = error?.config;

  if (error?.response?.status !== 401 || !canRefreshRequest(originalRequest)) {
    return Promise.reject(error);
  }

  originalRequest._authRefreshRetried = true;
  originalRequest.withCredentials = true;

  try {
    await validateAndRefreshIfRequired({ force: true });
    return axios.request(originalRequest);
  } catch {
    return Promise.reject(error);
  }
};

export const installAuthInterceptor = () => {
  if (requestInterceptorId === null) {
    requestInterceptorId = axios.interceptors.request.use(prepareProtectedRequest);
  }

  if (responseInterceptorId !== null) return responseInterceptorId;

  responseInterceptorId = axios.interceptors.response.use(
    (response) => response,
    retryAfterValidationAndRefresh
  );

  return responseInterceptorId;
};

export const resetAuthInterceptorForTests = () => {
  if (requestInterceptorId !== null) {
    axios.interceptors.request.eject(requestInterceptorId);
    requestInterceptorId = null;
  }

  if (responseInterceptorId !== null) {
    axios.interceptors.response.eject(responseInterceptorId);
    responseInterceptorId = null;
  }
};
